import { type FC, useMemo } from 'react';
import {
  UiStack,
  UiText,
  UiIconCurrencyCircleDollar,
  uiStyles,
  UiHStack,
  UiGrid,
  UiIconXCircle,
  UiTag,
  UiSpinner,
  UiVStack,
  UiWarp
} from '@/lib/ui';
import { type TempRegistration, type CartItem, type CartItemDeleteRequest, type CartItemUpdateRequest } from '@/api/registration';
import { registration } from '@/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { groupRegistrationQueryKey } from '@/api/registration/groupRegistration';

export interface ItemProps {
  itemId: number
  name: string
  priceFormat: string
  quantity: number
  cartId: number
  groupRegistrationId: number
  tempRegistrations: TempRegistration[]
  errors?: string[] | undefined
}

const Item: FC<ItemProps> = ({
  itemId,
  name,
  priceFormat,
  quantity,
  cartId,
  groupRegistrationId,
  tempRegistrations,
  errors
}) => {
  const queryClient = useQueryClient();

  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const { data: eventSetting } = useEventSettingsQuery(eventId);

  const { mutate: updateMutate, isLoading: updateMutateIsLoading } = useMutation<ApiResponse<CartItem>, Error, CartItemUpdateRequest>({
    mutationFn: async (data: CartItemUpdateRequest) => {
      return await registration.updateCartItem(createTenantApiRequest)(data);
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [registration.cartQueryKey, { groupRegistrationId }] }),
        queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }] }),
      ]);
    },
    onError: () => {
      // NOTE: handle error
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const { mutate: deleteMutate, isLoading: deleteMutateIsLoading } = useMutation<void, Error, CartItemDeleteRequest>({
    mutationFn: async (request: CartItemDeleteRequest) => {
      await registration.deleteCartItem(createTenantApiRequest)(request);
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [registration.cartQueryKey, { groupRegistrationId }] }),
        queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }] }),
      ]);
    },
    onError: () => {
      // NOTE: handle error
    }
  });

  const updateQuantity = (q: number) => {
    updateMutate({ id: itemId, quantity: q, cartId });
  };

  const deleteCartItem = () => {
    deleteMutate({ id: itemId });
  };

  return (
    <UiVStack alignItems={'stretch'}>
      <UiStack
        p={8}
        bgColor={'#fff'}
        borderRadius={uiStyles.borderRadius}
        shadow={uiStyles.cardShadow}
      >
        <UiGrid
          templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
          gap={8}
          alignItems={'flex-end'}
        >
          <UiStack spacing={4}>
            <UiText variant={'body1'} fontWeight={600}>{name}</UiText>
            <UiHStack justifyContent={'flex-start'}>
              <UiIconCurrencyCircleDollar size={'2xl'} color={'text.primary'} />
              <UiText>
              Price
              </UiText>
              <UiText>
                {priceFormat}
              </UiText>
            </UiHStack>
            <UiWarp justify={'left'}>
              {tempRegistrations?.map((tempRegistration, index) => {
                return (
                  <UiTag key={index} p={2} colorScheme="green" size={'sm'}>
                    {tempRegistration.firstName} {tempRegistration.lastName}
                  </UiTag>
                );
              })}
            </UiWarp>
            <UiHStack
              {...uiStyles.hover}
              pt={2}
              onClick={!deleteMutateIsLoading ? deleteCartItem : undefined}
            >
              {deleteMutateIsLoading ? <UiSpinner size="sm" /> : <UiIconXCircle color={'red.500'} size={'2xl'} />}
              <UiText color={'red.500'}>Remove</UiText>
            </UiHStack>
          </UiStack>
          <UiStack flexGrow={1}>
            <UiHStack
              spacing={4}
              alignItems={'center'}
              // justifyContent={{base: 'flex-end', lg: 'flex-end'}}
              justifyContent={'flex-end'}
            >
              {
                updateMutateIsLoading
                  ? <UiSpinner size="md" />
                  : <UiText variant={'title'}>{quantity}</UiText>
              }
            </UiHStack>
          </UiStack>
        </UiGrid>
      </UiStack>
      {
        errors && errors.length > 0 && (
          <UiStack flexGrow={1} paddingBottom={4}>
            {errors.map((error) => {
              return (
                <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
              );
            })}
          </UiStack>
        )}

    </UiVStack>

  );
};

export default Item;
